import { createAsyncThunk } from '@reduxjs/toolkit';
import { apiServiceInstanceQuestionBuilder } from '../apiUtils';
import { BASE_API_QUESTION_BUILDER } from '../../components/Common/Constants';
import { ExtractedQuestion } from '../Reducers/documentUploadReducer';
import { getToken } from 'utils';

// --- Payload Types ---

type ConfirmCreatePayload = {
  batchId: number;
  sectionId?: number;
  questions: ExtractedQuestion[];
};

// --- Extract Sections (non-streaming) ---

/**
 * POST to /documentUpload/extractSections to extract section headings from a document.
 * Returns the detected section names.
 */
export const extractSectionsFromDocument = async (
  payload: { batchId: number; fileUrl: string },
): Promise<{ status: string; sections: string[] }> => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const baseUrl = (BASE_API_QUESTION_BUILDER || '').replace(/\/+$/, '');
  const response = await fetch(`${baseUrl}/documentUpload/extractSections`, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify(payload),
  });

  if (!response.ok) throw new Error(`Extract sections failed: ${response.status}`);
  return response.json();
};

// --- SSE Streaming ---

/**
 * Streaming callbacks for SSE events from POST /documentUpload/stream.
 * Follows the same pattern as streamMultiQuestionMessage in multiQuestionPlaygroundActions.ts.
 */
export interface DocumentStreamCallbacks {
  onPhase: (phase: string, message: string) => void;
  onChunk: (content: string) => void;
  onQuestions: (questions: any[]) => void;
  onComplete: () => void;
  onError: (message: string) => void;
}

/**
 * POST to /documentUpload/stream and consume SSE events via ReadableStream.
 * EventSource cannot be used because the backend requires a POST body.
 *
 * Same pattern as streamMultiQuestionMessage() in multiQuestionPlaygroundActions.ts.
 */
export const streamDocumentProcessing = async (
  payload: {
    batchId: number;
    fileUrl: string;
    sectionId?: number;
    hasSection?: boolean;
    sections?: string[];
  },
  callbacks: DocumentStreamCallbacks,
  signal?: AbortSignal,
): Promise<void> => {
  const { onPhase, onChunk, onQuestions, onComplete, onError } = callbacks;

  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    const token = getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    console.log('[DocStream] Token present:', !!token);

    const baseUrl = (BASE_API_QUESTION_BUILDER || '').replace(/\/+$/, '');
    const streamUrl = `${baseUrl}/documentUpload/stream`;
    console.log('[DocStream] Connecting to:', streamUrl);
    console.log('[DocStream] Payload:', JSON.stringify(payload));

    const response = await fetch(
      streamUrl,
      {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(payload),
        signal,
      },
    );

    console.log('[DocStream] Response status:', response.status, response.statusText);
    console.log('[DocStream] Response headers:', Object.fromEntries(response.headers.entries()));

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Could not read error body');
      console.error('[DocStream] Request failed:', response.status, errorText);
      onError(`Stream request failed: ${response.status}`);
      return;
    }

    const reader = response.body?.getReader();
    if (!reader) {
      console.error('[DocStream] No readable stream in response body');
      onError('Stream not available');
      return;
    }

    console.log('[DocStream] Stream connected, reading events...');
    const decoder = new TextDecoder();
    let buffer = '';
    let eventCount = 0;

    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        console.log('[DocStream] Stream ended. Total events received:', eventCount);
        break;
      }

      const rawChunk = decoder.decode(value, { stream: true });
      buffer += rawChunk;

      // Parse SSE events from buffer (format: "data: {json}\n\n")
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // Keep incomplete line in buffer

      for (const line of lines) {
        const trimmed = line.trim();
        if (trimmed.startsWith('data: ')) {
          try {
            const data = JSON.parse(trimmed.slice(6));
            eventCount++;
            console.log(`[DocStream] Event #${eventCount}:`, data.type, data.type === 'phase' ? data.phase : '', data.type === 'chunk' ? `(${data.content?.length || 0} chars)` : '', data.type === 'questions' ? `(${data.questions?.length || 0} questions)` : '', data.type === 'error' ? data.message : '');
            switch (data.type) {
              case 'phase':
                onPhase(data.phase, data.message || '');
                break;
              case 'chunk':
                onChunk(data.content);
                break;
              case 'questions':
                console.log('[DocStream] Questions received:', JSON.stringify(data.questions).substring(0, 500));
                onQuestions(data.questions);
                break;
              case 'complete':
                onComplete();
                break;
              case 'error':
                onError(data.message || 'Processing error');
                break;
              default:
                console.warn('[DocStream] Unknown event type:', data.type, data);
            }
          } catch (parseErr) {
            console.warn('[DocStream] Failed to parse SSE line:', trimmed.substring(0, 200), parseErr);
          }
        }
      }
    }
  } catch (error: any) {
    if (error.name !== 'AbortError') {
      console.error('[DocStream] Connection error:', error.message, error);
      onError(error.message || 'Connection failed');
    } else {
      console.log('[DocStream] Stream aborted by user');
    }
  }
};

// --- Async Thunks ---

// Max questions per API call to avoid oversized payloads being cancelled
const CREATE_CHUNK_SIZE = 10;

/** Split an array into chunks of at most `size` elements */
function chunkArray<T>(arr: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
}

// Confirm Create Questions — chunked POST /documentUpload/create
export const confirmCreateQuestions = createAsyncThunk(
  'documentUpload/confirmCreate',
  async (payload: ConfirmCreatePayload) => {
    const questionsPayload = payload.questions.map((q) => ({
      id: q.id,
      text: q.text,
      type: q.type,
      options: q.options,
    }));

    const chunks = chunkArray(questionsPayload, CREATE_CHUNK_SIZE);
    console.log('[DocCreate] Creating', questionsPayload.length, 'questions in', chunks.length, 'chunk(s) for batchId:', payload.batchId, 'sectionId:', payload.sectionId);

    const allCreated: { tempId: string; questionId: number }[] = [];

    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i];
      console.log(`[DocCreate] Chunk ${i + 1}/${chunks.length}: ${chunk.length} questions`);

      const response = await apiServiceInstanceQuestionBuilder.post(
        '/documentUpload/create',
        {
          batchId: payload.batchId,
          sectionId: payload.sectionId,
          questions: chunk,
        },
      );

      console.log(`[DocCreate] Chunk ${i + 1} response:`, response.data);

      if (response.data?.created) {
        allCreated.push(...response.data.created);
      }
    }

    return { status: 'success', created: allCreated };
  },
);

export { chunkArray, CREATE_CHUNK_SIZE };
